<template>
  <div class="profile-container">
    <div class="profile-content">
      <el-card class="profile-card">
        <template #header>
          <div class="card-header">
            <div class="header-left">
              <div class="user-title">
                <h2>{{ userForm.username }}</h2>
                <span class="user-role">{{ userForm.realname }}</span>
              </div>
            </div>
            <div class="header-right">
              <el-button 
                type="primary" 
                link
                @click="handleEdit"
                v-if="!isEditing"
              >
                <el-icon><Edit /></el-icon>
                编辑
              </el-button>
              <div v-else class="edit-actions">
                <el-button 
                  type="primary" 
                  link
                  @click="handleSave"
                >
                  <el-icon><Check /></el-icon>
                  保存
                </el-button>
                <el-button 
                  type="info" 
                  link
                  @click="handleCancel"
                >
                  <el-icon><Close /></el-icon>
                  取消
                </el-button>
              </div>
            </div>
          </div>
        </template>
        <div class="profile-info">
          <div class="info-section">
            <h3>基本信息</h3>
            <el-form :model="userForm" label-width="100px">
              <el-form-item label="账号">
                <el-input v-model="userForm.username" disabled />
              </el-form-item>
              <el-form-item label="昵称">
                <el-input 
                  v-model="userForm.realname" 
                  :disabled="!isEditing"
                  placeholder="请输入昵称"
                />
              </el-form-item>
            </el-form>
          </div>
          <div class="info-section">
            <h3>安全设置</h3>
            <div class="security-item">
              <div class="security-info">
                <el-icon :size="24"><Lock /></el-icon>
                <div class="security-text">
                  <div class="security-title">登录密码</div>
                  <div class="security-desc">定期修改密码可以保护账号安全</div>
                </div>
              </div>
              <el-button 
                type="primary" 
                @click="showPasswordDialog = true"
              >
                修改密码
              </el-button>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 修改密码弹窗 -->
    <el-dialog
      v-model="showPasswordDialog"
      title="修改密码"
      width="500px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <el-form 
        :model="passwordForm" 
        :rules="passwordRules" 
        ref="passwordFormRef" 
        label-width="100px"
      >
        <el-form-item label="原密码" prop="oldPassword">
          <el-input 
            v-model="passwordForm.oldPassword" 
            type="password" 
            show-password 
            placeholder="请输入原密码"
          />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input 
            v-model="passwordForm.newPassword" 
            type="password" 
            show-password 
            placeholder="请输入新密码"
          />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input 
            v-model="passwordForm.confirmPassword" 
            type="password" 
            show-password 
            placeholder="请再次输入新密码"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showPasswordDialog = false">取消</el-button>
          <el-button type="primary" @click="handleUpdatePassword">
            确认修改
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useUserStore } from '@/store/modules/user'
import { ElMessage } from 'element-plus'
import { User, Edit, Check, Close, Lock } from '@element-plus/icons-vue'
import encrypt from '@/utils/encrypt'

const userStore = useUserStore()
const passwordFormRef = ref(null)
const isEditing = ref(false)
const showPasswordDialog = ref(false)
const originalRealname = ref('')

const userForm = reactive({
  username: userStore.userInfo?.username || '',
  realname: userStore.userInfo?.realname || ''
})

const passwordForm = reactive({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const validatePass = (rule, value, callback) => {
  if (value === '') {
    callback(new Error('请输入密码'))
  } else {
    if (passwordForm.confirmPassword !== '') {
      passwordFormRef.value?.validateField('confirmPassword')
    }
    callback()
  }
}

const validatePass2 = (rule, value, callback) => {
  if (value === '') {
    callback(new Error('请再次输入密码'))
  } else if (value !== passwordForm.newPassword) {
    callback(new Error('两次输入密码不一致!'))
  } else {
    callback()
  }
}

const passwordRules = {
  oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
  newPassword: [
    { required: true, validator: validatePass, trigger: 'blur' },
    {
      pattern: /^.{6,}$/,
      message: '密码由6位符号组成!',
      trigger: 'blur'
    },
  ],
  confirmPassword: [
    { required: true, validator: validatePass2, trigger: 'blur' }
  ]
}

const handleEdit = () => {
  isEditing.value = true
  originalRealname.value = userForm.realname
}

const handleCancel = () => {
  isEditing.value = false
  userForm.realname = originalRealname.value
}

const handleSave = async () => {
  try {
    const res = await userStore.updateUserInfo({ id: userStore.userInfo?.id, realname: userForm.realname })
    if (res) {
      ElMessage.success('昵称修改成功')
      isEditing.value = false
    }
  } catch (error) {
    ElMessage.error('更新失败：' + error.message)
  }
}

const handleUpdatePassword = async () => {
  if (!passwordFormRef.value) return
  
  await passwordFormRef.value.validate(async (valid) => {
    if (valid) {
      try {
        const res = await userStore.updatePassword({
          username: userStore.userInfo?.username,
          oldPassword: encrypt(passwordForm.oldPassword),
          newPassword: encrypt(passwordForm.newPassword)
        })
        if (res) {
          ElMessage.success('密码修改成功')
          showPasswordDialog.value = false
          // 清空表单
          passwordForm.oldPassword = ''
          passwordForm.newPassword = ''
          passwordForm.confirmPassword = ''
        }
      } catch (error) {
        ElMessage.error('密码修改失败：' + error.message)
      }
    }
  })
}
</script>

<style scoped lang="scss">
.profile-container {
  width: 100%;
  height: 100%;
  padding: 24px;
  background-color: #f5f7fa;
  box-sizing: border-box;
  
  .profile-content {
    height: 100%;
    max-width: 1000px;
    margin: 0 auto;
  }

  .profile-card {
    height: 100%;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);

    :deep(.el-card__header) {
      padding: 24px;
      border-bottom: 1px solid #ebeef5;
    }

    :deep(.el-card__body) {
      padding: 24px;
      height: calc(100% - 113px);
      overflow-y: auto;
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .header-left {
        display: flex;
        align-items: center;
        gap: 16px;

        .user-title {
          h2 {
            margin: 0;
            font-size: 20px;
            color: #303133;
          }

          .user-role {
            font-size: 14px;
            color: #909399;
            margin-top: 4px;
          }
        }
      }

      .header-right {
        .edit-actions {
          display: flex;
          gap: 8px;
        }
      }
    }

    .profile-info {
      .info-section {
        margin-bottom: 32px;

        h3 {
          margin: 0 0 16px;
          font-size: 16px;
          color: #303133;
          font-weight: 500;
        }

        .security-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px;
          background: #f5f7fa;
          border-radius: 4px;

          .security-info {
            display: flex;
            align-items: center;
            gap: 12px;

            .security-text {
              .security-title {
                font-size: 14px;
                color: #303133;
                margin-bottom: 4px;
              }

              .security-desc {
                font-size: 12px;
                color: #909399;
              }
            }
          }
        }
      }
    }
  }
}

:deep(.el-dialog__body) {
  padding: 24px 24px 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 0;
}
</style> 